// Lib
import { createSelector } from 'reselect';

// Utils
import { prop, propIn } from '../../../common/utils/immutableHelper';

// Selectors
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';

// Constants
import * as TYPES from '../../../common/elements/elementConstants';

// Attachment getters
export const getData = prop('data');
export const getError = prop('error');
export const getFetching = prop('fetching');
export const getFetched = prop('fetched');
export const getPercentageComplete = prop('percentageComplete');
export const getLoaded = prop('loaded');
export const getTotal = prop('total');

// File
export const getDataType = propIn(['data', 'type']);
export const getDataFilename = propIn(['data', 'filename']);
export const getDataFilesize = propIn(['data', 'size']);
export const getDataUri = propIn(['data', 'dataUri']);
export const getDataIsEditPreview = propIn(['data', 'isEditPreview']);

export const isEditPreviewAttachmentUploading = (attachment) =>
    getFetching(attachment) && getDataIsEditPreview(attachment);

export const isEditPreviewAttachmentFetched = (attachment) =>
    getFetched(attachment) && getDataIsEditPreview(attachment);

export const getInitialAttachmentTransferProgress = () => ({ count: 0, total: 0, loaded: 0, showProgress: false });

export const attachmentTransferProgressReducer = (acc, attachment) => {
    if (!attachment) return acc;

    acc.count += 1;
    acc.actionType = attachment.get('actionType');
    // Only show progress if we know how much is being loaded
    if (attachment.get('loaded')) {
        acc.showProgress = true;
        acc.total += attachment.get('total');
        acc.loaded += attachment.get('loaded');
    }

    return acc;
};

export const isAttachmentActionType = (actionType) => (attachment) =>
    attachment.get('fetching') && attachment.get('actionType') === actionType;

export const getAttachmentsProgressByActionType = (attachments, actionType) =>
    attachments
        .filter(isAttachmentActionType(actionType))
        .reduce(attachmentTransferProgressReducer, getInitialAttachmentTransferProgress());

export const getAttachments = (state) => state.getIn(['app', 'attachments']);

export const getAttachmentForElement = (state, elementId) => state.getIn(['app', 'attachments', elementId]);

export const isUploadingAttachment = createSelector(getAttachments, (attachments) =>
    attachments.some((attachment) => attachment.get('fetching')),
);

export const hasAttachmentsUploads = createSelector(getAttachments, (attachments) =>
    attachments.some(
        (attachment) =>
            attachment.get('fetching') && attachment.get('actionType') === TYPES.ELEMENT_ATTACHMENT_ACTION_TYPES.UPLOAD,
    ),
);

export const hasAttachmentsDownloads = createSelector(getAttachments, (attachments) =>
    attachments.some(
        (attachment) =>
            attachment.get('fetching') &&
            attachment.get('actionType') === TYPES.ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD,
    ),
);

export const selectIsUploadingAttachmentWithProgress = createSelector(
    getAttachments,
    isUploadingAttachment,
    (attachments, isUploading) => isUploading && attachments.some((attachment) => attachment.get('loaded')),
);

export const getAttachmentErrors = createSelector(getAttachments, (attachments) =>
    attachments.filter(getError).map(getError),
);
export const allDownloadsSelector = createShallowSelector(getAttachments, (attachments) =>
    getAttachmentsProgressByActionType(attachments, TYPES.ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD),
);

export const allUploadsSelector = createShallowSelector(getAttachments, (attachments) =>
    getAttachmentsProgressByActionType(attachments, TYPES.ELEMENT_ATTACHMENT_ACTION_TYPES.UPLOAD),
);

export const getAttachmentProgress = createShallowSelector(
    getAttachments,
    (state, ownProps) => ownProps.elementId,
    (attachments, elementId) =>
        attachments
            .filter((attachment) => attachment.get('id') === elementId)
            .reduce(attachmentTransferProgressReducer, getInitialAttachmentTransferProgress()),
);
